import {useRef, useEffect, useState} from 'react';
import {useController, useFormContext} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {useSettings} from '@ui/settings/use-settings';

interface Props {
  name: string;
  label: React.ReactNode;
  description?: React.ReactNode;
}

declare global {
  interface Window {
    google: any;
    initGoogleMaps: () => void;
  }
}

export function GoogleMapsAddressInput({name, label, description}: Props) {
  const {google_maps} = useSettings();
  const {control} = useFormContext();
  const {field} = useController({name, control});
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const displayFieldName = `${name}_display`;

  useEffect(() => {
    // Parse existing address JSON to display value
    if (field.value) {
      try {
        const address = JSON.parse(field.value);
        if (address.street && address.city) {
          setDisplayValue(`${address.street_number || ''} ${address.street}, ${address.neighborhood || ''}, ${address.city} - ${address.state}, ${address.country}`.trim());
        }
      } catch (e) {
        // Invalid JSON, keep empty
      }
    }
  }, [field.value]);

  useEffect(() => {
    if (!google_maps?.api_key || isLoaded) return;

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${google_maps.api_key}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    window.initGoogleMaps = () => {
      setIsLoaded(true);
    };

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      delete window.initGoogleMaps;
    };
  }, [google_maps?.api_key, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !window.google) return;

    // Find the actual input element by name attribute
    const inputElement = document.querySelector(`input[name="${displayFieldName}"]`) as HTMLInputElement;
    if (!inputElement) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: {country: 'BR'}, // Restrict to Brazil
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry) return;

      // Extract address components
      const addressComponents = place.address_components || [];
      const addressData: any = {
        street_number: '',
        street: '',
        neighborhood: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
      };

      addressComponents.forEach((component: any) => {
        const types = component.types;
        
        if (types.includes('street_number')) {
          addressData.street_number = component.long_name;
        } else if (types.includes('route')) {
          addressData.street = component.long_name;
        } else if (types.includes('sublocality') || types.includes('neighborhood')) {
          addressData.neighborhood = component.long_name;
        } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          addressData.city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          addressData.state = component.short_name;
        } else if (types.includes('country')) {
          addressData.country = component.long_name;
        } else if (types.includes('postal_code')) {
          addressData.postal_code = component.long_name;
        }
      });

      // Update form field with JSON data
      field.onChange(JSON.stringify(addressData));
      
      // Update display value
      setDisplayValue(place.formatted_address || '');
    });

  }, [isLoaded, field, displayFieldName]);

  if (!google_maps?.api_key) {
    return (
      <FormTextField
        name={name}
        label={label}
        description="Google Maps API key not configured"
        disabled
      />
    );
  }

  return (
    <FormTextField
      name={displayFieldName}
      label={label}
      description={description}
      value={displayValue}
      onChange={(e) => setDisplayValue(e.target.value)}
      placeholder="Digite seu endereço..."
      disabled={!isLoaded}
    />
  );
}
